import { Chart, ChartData, ChartDataset, ChartOptions, Filler, Plugin, Scale, TooltipItem } from "chart.js";
import { useCallback, useMemo, useState } from "react";

import { ColorConstants } from "../../../constants";
import { ChartDatasets } from "../models";

interface UseLineChartReturnData {
  chartData: ChartData<"line", number[], string>;
  options: ChartOptions<"line">;
  plugins: Plugin<"line">[] | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lineChartRef: (node: any) => void;
}

interface UseLineChartProps {
  showLegend?: boolean;
  labels: string[];
  datasets: ChartDatasets;
  chartOffset?: number;
  customChartColours?: string[];
  xAxisTickOffset?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisTickCallbackFunction?: (this: Scale, val: string | number, index: number) => any;
  isGradient?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lineChartRef?: (node: any) => void;
  labelCallbackFunction?: (context: TooltipItem<"line">) => string;
}

export const useLineChart = ({
  showLegend = false,
  labels,
  datasets,
  chartOffset,
  customChartColours = ColorConstants.LINE_CHART_COLORS,
  xAxisTickOffset,
  xAxisTitle,
  yAxisTitle,
  yAxisTickCallbackFunction,
  isGradient = true,
  labelCallbackFunction = (context: TooltipItem<"line">) => {
    const x = context.label ?? (context.parsed && context.parsed.x);
    const y = context.parsed && context.parsed.y;
    return `${String(x)} - ${Number(y).toLocaleString()}`;
  },
}: UseLineChartProps): UseLineChartReturnData => {
  const [chartData, setChartData] = useState<ChartData<"line", number[], string>>({ datasets: [] });

  const lineChartRef = useCallback(
    (node: Chart<"line">) => {
      if (!node || datasets.length === 0) return;

      const applyGradients = (): (CanvasGradient | string)[] => {
        const { ctx, chartArea } = node;

        const gradientsArr: (CanvasGradient | string)[] = [];

        const chartMaxValue = Math.max(...datasets.flatMap((x) => x.data));

        // Calculate the height of each line based on values and the charts actual height, then create the linear gradient based on that
        datasets.forEach((dataset: ChartDataset<"line", number[]>, i: number) => {
          const base = customChartColours[i] || ColorConstants.GREY_500_RGB;

          const localMaxValue = Math.max(...dataset.data);
          const localDataRatio = localMaxValue / chartMaxValue;

          const chartHeight = chartArea.bottom - chartArea.top;
          const clampedRatio = Math.max(0, Math.min(1, localDataRatio));

          const localTop = chartArea.top + (1 - clampedRatio) * chartHeight;

          if (!Number.isNaN(localTop)) {
            const g = ctx.createLinearGradient(0, localTop, 0, chartArea.bottom);
            g.addColorStop(0, `rgba(${base}, 0.3)`);
            g.addColorStop(0.9, `rgba(${base}, 0)`);

            gradientsArr.push(g);
          }
        });

        return gradientsArr;
      };

      const gradients = applyGradients();

      setChartData({
        labels: labels.map((x) => x),
        datasets: datasets.map((x, i) => ({
          ...x,
          backgroundColor: gradients[i] ?? `rgb(${customChartColours[i]})`,
          borderColor: `rgb(${customChartColours[i]})`,
          fill: isGradient,
        })),
      });
    },
    [datasets, customChartColours, isGradient, labels]
  );

  const plugins = useMemo(() => {
    const tempPlugins = [
      {
        // No default way to change this, so using this - https://stackoverflow.com/a/67723827
        id: "increaseLegendSpacing",
        beforeInit: (chart: Chart) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const originalFit = (chart.legend as any).fit;

          // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any
          (chart.legend as any).fit = function fit() {
            // Call original function and bind scope in order to use `this` correctly inside it
            originalFit.bind(chart.legend)();
            // eslint-disable-next-line react/no-this-in-sfc
            this.height += 16;
          };
        },
      },
    ] as Plugin<"line">[] | undefined;

    // Adding offset to each point on the line chart
    if (chartOffset)
      tempPlugins?.push(
        {
          id: "onInitOffset",
          beforeDraw: (chart: Chart) => {
            chart.data.datasets.forEach((dataset: ChartDataset, i: number) => {
              const meta = chart.getDatasetMeta(i);
              for (let j = 0; j < meta.data.length; j++) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const point = meta.data[j] as any;
                if (point.old_x === undefined) {
                  point.old_x = point.x;
                }
                point.x = point.old_x + chartOffset;
              }
            });
          },
        },
        {
          id: "onResizeOffset",
          resize: (chart: Chart) => {
            chart.data.datasets.forEach((dataset: ChartDataset, i: number) => {
              const meta = chart.getDatasetMeta(i);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              meta.data.forEach((point: any) => {
                if (point.old_x !== undefined) {
                  // eslint-disable-next-line no-param-reassign
                  point.old_x = undefined;
                }
              });
            });
          },
        }
      );

    /* Note - register Filler plugin here, rather than in maybeSetGlobalChartJsConfiguration, 
       as when we add it before our custom plugins it causes the chart to glitch. Also we only need when chart needs fill
     */
    tempPlugins?.push(Filler);

    return tempPlugins;
  }, [datasets]);

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: 4,
      elements: {
        point: {
          radius: 0,
          hitRadius: 10,
        },
        line: {
          tension: 0,
          borderWidth: 2,
        },
      },
      scales: {
        x: {
          // type: "category",
          offset: false,
          title: xAxisTitle
            ? {
                display: true,
                text: xAxisTitle,
                font: {
                  size: 14,
                  weight: 500,
                  family: "figtree",
                },
                padding: 10,
              }
            : undefined,
          ticks: {
            font: {
              family: "figtree",
            },
            padding: 8,
            labelOffset: xAxisTickOffset ?? chartOffset,
            align: "start",
          },
          grid: {
            display: false,
            drawTicks: false,
          },
        },
        y: {
          title: yAxisTitle
            ? {
                display: true,
                text: yAxisTitle,
                padding: 12,
                font: {
                  size: 14,
                  weight: 500,
                  family: "figtree",
                },
              }
            : undefined,
          grid: {
            display: true,
            drawTicks: false,
          },
          border: {
            display: false,
          },
          ticks: {
            callback: yAxisTickCallbackFunction,
            font: {
              family: "figtree",
            },
            padding: 8,
          },
        },
      },
      plugins: {
        legend: {
          display: showLegend,
          align: "start",
          position: "top",
          labels: {
            boxHeight: 12,
            boxWidth: 12,
            borderRadius: 2,
            useBorderRadius: true,
            padding: 16,
            color: ColorConstants.GREY_300,
            font: {
              family: "figtree",
              size: 14,
            },
          },
        },
        title: {
          display: false,
        },
        tooltip: {
          displayColors: false,
          callbacks: {
            // items is an array of TooltipItem<"line">
            title: (items: TooltipItem<"line">[]) => {
              if (!items || items.length === 0) return "";
              // show the dataset label (from the first item)
              return String(items[0].dataset.label ?? items[0].label ?? "");
            },
            // context is a single TooltipItem<"line">
            label: labelCallbackFunction,
          },
        },
        filler: {
          propagate: true,
        },
      },
    }),
    [datasets]
  );

  return {
    options,
    chartData,
    plugins,
    lineChartRef,
  };
};
